import "@/lib/sentry";
import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { CookieBanner } from "@/components/CookieBanner";
import { SentryBoundary } from "@/components/SentryBoundary";
import appCss from "../styles.css?url";
import lightLogo from "@/assets/wa-light.png";
import darkLogo from "@/assets/wa-dark.png";

const themeInitScript = `(function(){try{var t=localStorage.getItem('tapeready-theme');if(t!=='light'&&t!=='dark'){t='light';}var r=document.documentElement;if(t==='dark'){r.classList.add('dark');}r.style.colorScheme=t;}catch(e){}})();`;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold font-mono text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          That route is off-camera.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:opacity-90"
          >
            Back to RoleBoard
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "RoleBoard - The Operating System for Actors" },
      {
        name: "description",
        content:
          "The free audition tracker for actors. Organise your self-tapes, track callbacks, and start understanding your career.",
      },
      { property: "og:title", content: "RoleBoard - The Operating System for Actors" },
      {
        property: "og:description",
        content:
          "The free audition tracker for actors. Organise your self-tapes, track callbacks, and start understanding your career.",
      },
      { property: "og:url", content: "https://roleboard.app" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "RoleBoard - The Operating System for Actors" },
      { name: "twitter:description", content: "The free audition tracker for actors. Organise your self-tapes, track callbacks, and start understanding your career." },
      { property: "og:image", content: "https://roleboard.app/og-image.jpg" },
      { name: "twitter:image", content: "https://roleboard.app/og-image.jpg" },
      { property: "og:site_name", content: "RoleBoard" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preload", as: "image", href: lightLogo },
      { rel: "preload", as: "image", href: darkLogo },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=4" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-light.png?v=4" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=4" },
    ],
    scripts: [
      { children: themeInitScript },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://roleboard.app/#organization",
              name: "RoleBoard",
              url: "https://roleboard.app",
              email: "hello@roleboard.app",
            },
            {
              "@type": "WebSite",
              "@id": "https://roleboard.app/#website",
              url: "https://roleboard.app",
              name: "RoleBoard",
              description:
                "The operating system for actors — track auditions, organize self tapes, and understand your acting career.",
              publisher: { "@id": "https://roleboard.app/#organization" },
            },
            {
              "@type": "WebApplication",
              name: "RoleBoard",
              url: "https://roleboard.app",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              description:
                "Track auditions, organize self tapes, and visualize your acting career in one private workspace.",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Toaster />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <SentryBoundary>
      <ThemeProvider>
        <AuthProvider>
          <main id="main">
            <Outlet />
          </main>
          <CookieBanner />
        </AuthProvider>
      </ThemeProvider>
    </SentryBoundary>
  );
}
